// Top-ups are merchants funding their own wallet. They appear in the
// transaction report but are not collections — there is no merchant to settle
// to. The TOPUP- prefix is checked on tranId and preferenceNo as well as
// paymentLinkType, because the marker lands on different fields per record.
export const isTopUpTransaction = (transaction) => {
  if (!transaction) return false;
  if (transaction.paymentLinkType === "TOPUP_PAYMENT_LINK") return true;
  return (
    String(transaction.tranId ?? "").startsWith("TOPUP-") ||
    String(transaction.preferenceNo ?? "").startsWith("TOPUP-")
  );
};

// From the transactions module.
export const getTransactionSettlementEligibility = (transaction) => {
  if (!transaction) return { eligible: false, reason: "No transaction selected." };

  if (isTopUpTransaction(transaction)) {
    return {
      eligible: false,
      reason: "Top-ups are merchant wallet funding, not collections, and are never settled.",
    };
  }

  // Settling a transaction that never succeeded would pay out money that was
  // never collected.
  if (transaction.status !== "SUCCESSFUL") {
    return {
      eligible: false,
      reason: "Only a successful transaction can be settled — funds were not collected on this one.",
    };
  }

  if (transaction.settlementStatus !== "PENDING") {
    return {
      eligible: false,
      reason: `Settlement is already ${String(transaction.settlementStatus ?? "unknown").toLowerCase()}.`,
    };
  }

  return { eligible: true, reason: null, notice: null };
};

// From the settlements module. A settlement record already represents a
// collection awaiting payout, so the check is about its current state only.
export const getSettlementManualEligibility = (settlement) => {
  if (!settlement) return { eligible: false, reason: "No settlement selected." };

  const status = settlement.settlementStatus;

  if (status === "SETTLED") {
    return { eligible: false, reason: "This settlement has already been settled." };
  }

  if (status === "REFUNDED") {
    return { eligible: false, reason: "This settlement was refunded and cannot be settled." };
  }

  if (!settlement.paymentRefNo) {
    return {
      eligible: false,
      reason: "This record has no payment reference, which the settlement endpoint requires.",
    };
  }

  // Eligible, but worth saying out loud before the admin confirms.
  let notice = null;
  if (status === "AWAITING24HRS") {
    notice = "This settlement is inside its 24-hour hold. Settling now bypasses that hold.";
  } else if (settlement.payoutStatus === "PAID") {
    notice = "Payout on this record is already marked PAID. Confirm funds have not already moved.";
  }

  return { eligible: true, reason: null, notice };
};